/**
 * usePermissions hook
 * Provides permission checking utilities for RBAC UI enforcement
 * Used to conditionally show/hide UI elements based on user permissions
 */
import { useCurrentUser } from './useCurrentUser'

/**
 * Permission values as stored in the user's permissions object
 */
export type PermissionValue = boolean | 'all' | 'own' | 'none'

/**
 * Hook for checking user permissions
 * Wraps useCurrentUser and provides convenience methods for permission checks
 * 
 * @example
 * ```tsx
 * const { hasPermission, canAccessAll, role } = usePermissions()
 * 
 * // Check if user has any level of access to a permission
 * if (hasPermission('config:edit')) { ... }
 * 
 * // Check if user has "all" access (not just "own")
 * if (canAccessAll('session:view')) { ... }
 * ```
 */
export function usePermissions() {
  const { data: user, isLoading } = useCurrentUser()

  /**
   * Check if user has any level of access to a permission
   * Returns true for: true, "all", "own"
   * Returns false for: false, "none", undefined
   */
  const hasPermission = (permission: string): boolean => {
    if (!user?.permissions) return false
    const value = user.permissions[permission]
    return value === true || value === 'all' || value === 'own'
  }

  /**
   * Check if user has unrestricted ("all") access to a permission
   * Returns true for: true, "all"
   * Returns false for: "own", false, "none", undefined
   */
  const canAccessAll = (permission: string): boolean => {
    if (!user?.permissions) return false
    const value = user.permissions[permission]
    return value === 'all' || value === true
  }

  return {
    hasPermission,
    canAccessAll,
    isLoading,
    role: user?.role,
  }
}
